import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const destinations = [
  {
    name: 'Bali, Indonesia',
    tagline: 'Island of the Gods',
    packages: 12,
    image: 'https://public.readdy.ai/ai/img_res/00acae82e6264ac8fbfc0524d01d31d4.jpg',
  },
  {
    name: 'Japan',
    tagline: 'Land of Rising Sun',
    packages: 9,
    image: 'https://readdy.ai/api/search-image?query=Kyoto%20Japan%20traditional%20geisha%20district%20Gion%20with%20cherry%20blossom%20trees%20lining%20cobblestone%20path%2C%20lanterns%2C%20wooden%20machiya%20townhouses%2C%20magical%20spring%20atmosphere&width=400&height=500&seq=1010&orientation=portrait',
  },
  {
    name: 'Europe',
    tagline: 'Timeless & Magnificent',
    packages: 15,
    image: 'https://readdy.ai/api/search-image?query=Santorini%20Greece%20iconic%20white-washed%20buildings%20blue%20domed%20churches%20overlooking%20deep%20blue%20Aegean%20sea%2C%20cliffside%20village%2C%20stunning%20Mediterranean%20scenery%20travel%20photography&width=400&height=500&seq=1013&orientation=portrait',
  },
  {
    name: 'Maldives',
    tagline: 'Tropical Water Paradise',
    packages: 7,
    image: 'https://readdy.ai/api/search-image?query=Maldives%20aerial%20view%20of%20tiny%20tropical%20coral%20island%20with%20overwater%20bungalows%20turquoise%20lagoon%20and%20deep%20blue%20ocean%2C%20paradise%20destination%2C%20luxury%20resort%20photography&width=400&height=500&seq=1012&orientation=portrait',
  },
];

export default function PopularDestinations() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-amber-500 font-semibold text-sm uppercase tracking-widest mb-3">{t('destinations.badge')}</p>
          <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
            {t('destinations.title')}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            {t('destinations.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {destinations.map((dest, idx) => (
            <Link
              key={dest.name}
              to="/tours"
              className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${idx * 120}ms` }}
            >
              <div className="w-full h-80">
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-amber-400 text-xs font-medium mb-1">{dest.tagline}</p>
                <h3 className="text-white font-bold text-lg font-playfair">{dest.name}</h3>
                <p className="text-white/70 text-xs mt-1">
                  <i className="ri-map-pin-line mr-1"></i>{dest.packages} {t('destinations.packages')}
                </p>
                <div className="mt-3 flex items-center gap-1 text-white/0 group-hover:text-white/90 transition-all duration-300 text-xs font-medium">
                  <span>{t('destinations.explore')}</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
